import type { ActivityOf } from '@wyn/content';
import { METALS, type SmithingSim, smithingSim } from '@wyn/game-core';
import { HoldButton, Progress, Stage, Track } from './parts.tsx';
import type { LiveGameProps } from './registry.tsx';
import { useRealtime } from './useRealtime.ts';

/** Glowing colour of hot metal, from dull grey through cherry to white. */
const heatColor = (t: number) => {
  if (t < 25) return '#5a5650';
  const k = (t - 25) / 75;
  return `hsl(${Math.round(k * 50)},90%,${Math.round(18 + k * 62)}%)`;
};

const HINT = {
  heat: (metal: string) => (
    <>Hold <b>Pump bellows</b> (or Space) to keep the {metal} in the green band until it is workable. The coals respond slowly, so feather it. The hatched zone scorches.</>
  ),
  shape: (n: number) => <>Strike (or Space) as the hammer crosses the green mark. {n} blows. Cold metal takes blows poorly.</>,
  quench: () => <>One last heat, then it cools in the air. <b>Quench</b> (or Space) as the needle passes through the blue band.</>,
};

/** The Great Forge: heat, hammer, quench. */
export function SmithingGame({ session, activity, onDone }: LiveGameProps) {
  const a = activity as ActivityOf<'smithing'>;
  const rt = useRealtime<SmithingSim>({
    create: () => smithingSim(a, session.level, session.seed!),
    onDone: (_sim, trace) => onDone(trace),
  });
  const sim = rt.sim;
  if (!sim) return null;
  const metal = METALS[a.metal].name.toLowerCase();
  const phase = sim.phase === 'pause-shape' ? 'shape' : sim.phase === 'pause-quench' ? 'quench' : sim.phase;
  const col = heatColor(sim.temp);
  const glow = Math.max(0, Math.min(1, (sim.temp - 25) / 75));
  const lastStrike = [...sim.fx].reverse().find((f) => f.cue === 'strike');
  const struck = lastStrike && sim.tick - lastStrike.tick < 8;

  return (
    <Stage fx={rt.fx}>
      <div className="flex justify-between text-xs text-ink-faint">
        <span className="smallcaps">{phase === 'heat' ? '1/3 · Heat' : phase === 'shape' ? '2/3 · Shape' : '3/3 · Quench'}</span>
        <span className="font-mono">{Math.round(sim.temp * 14)}°C</span>
      </div>
      <p className="text-sm text-ink-dim">{phase === 'heat' ? HINT.heat(metal) : phase === 'shape' ? HINT.shape(a.strikes) : HINT.quench()}</p>
      <div className="flex h-28 flex-col items-center justify-end">
        <div
          className="h-6 w-[55%] max-w-[260px] rounded-[5px] transition-transform duration-75"
          style={{ background: col, boxShadow: glow > 0 ? `0 0 ${6 + glow * 34}px ${col}` : 'none', transform: struck ? 'translateY(2px) scaleY(0.9)' : undefined }}
        />
        <div className="h-9 w-[62%] max-w-[300px] bg-gradient-to-b from-[#4b4a48] to-[#2b2a28] [clip-path:polygon(0_0,100%_0,88%_45%,80%_100%,20%_100%,12%_45%)]" />
      </div>

      {phase === 'heat' && (
        <>
          <Track value={sim.temp} band={[...sim.zone]} danger={sim.burnAt} label="Heat" />
          <Progress value={sim.heatProgress} label="Workability" />
        </>
      )}
      {phase === 'shape' && (
        <>
          <Track
            value={sim.pos * 100}
            label="Hammer"
            zones={[
              { from: (sim.center - sim.good / 2) * 100, to: (sim.center + sim.good / 2) * 100, className: 'bg-gilt/30' },
              { from: (sim.center - sim.perfect / 2) * 100, to: (sim.center + sim.perfect / 2) * 100, className: 'bg-verdigris/70' },
            ]}
          />
          <div className="flex justify-center gap-1.5" aria-label="Blows">
            {Array.from({ length: a.strikes }, (_, i) => {
              const q = sim.strikes[i];
              return <i key={i} className={`h-2.5 w-2.5 rounded-full ${q === undefined ? 'bg-rule' : q >= 1 ? 'bg-verdigris' : q >= 0.5 ? 'bg-gilt' : 'bg-blood'}`} />;
            })}
          </div>
        </>
      )}
      {phase === 'quench' && <Track value={Math.max(0, sim.temp)} band={[...sim.quenchBand]} label="Heat" bandClass="bg-woad/45" />}

      <HoldButton held={rt.isHeld()} {...rt.holdProps()} disabled={!!sim.result}>
        {phase === 'heat' ? '🔥 Pump bellows' : phase === 'shape' ? '🔨 Strike' : '💧 Quench'}
      </HoldButton>
    </Stage>
  );
}
